import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Separator } from './ui/separator';
import { Alert } from './ui/alert';
import {
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { cn } from '../lib/utils';

const BulkSetupWizardNew = ({ isOpen, onClose, onComplete }) => {
  const [step, setStep] = useState(1);
  const [selectedOption, setSelectedOption] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('balanced');
  const [templates, setTemplates] = useState([]);
  const [showAdvancedProducts, setShowAdvancedProducts] = useState(false);
  const [showAdvancedRule, setShowAdvancedRule] = useState(false);
  const [customRule, setCustomRule] = useState({
    threshold: 10,
    comparison: 'average'
  });
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [summary, setSummary] = useState(null);
  const { getToken } = useAuth();

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
      fetchProducts();
      fetchTemplates();
    }
  }, [isOpen]);

  const fetchCategories = async () => {
    try {
      const token = getToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/categories-with-count`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const token = getToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/dashboard/products?limit=100`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAllProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = getToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/pricing/templates`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleNext = () => {
    if (step === 1) {
      if (selectedOption === 'custom' && selectedProducts.length === 0) {
        toast.warning('لطفاً حداقل یک محصول انتخاب کنید');
        return;
      }
      if (selectedOption === 'category' && !selectedCategory) {
        toast.warning('لطفاً یک دسته انتخاب کنید');
        return;
      }
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    try {
      setProcessing(true);
      setStep(3);

      const token = getToken();
      let productIds = [];

      if (selectedOption === 'all') {
        productIds = allProducts.map(p => p.id);
      } else if (selectedOption === 'category') {
        productIds = allProducts
          .filter(p => p.category_id === selectedCategory)
          .map(p => p.id);
      } else {
        productIds = selectedProducts;
      }

      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 500);

      const payload = {
        product_ids: productIds,
        max_competitors_per_product: 5
      };

      if (showAdvancedRule) {
        payload.custom_rule = customRule;
      } else {
        payload.template_key = selectedTemplate;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/bulk-setup`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      clearInterval(progressInterval);
      setProgress(100);
      setSummary(response.data.summary);
      toast.success(response.data.message);

      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error('Error in bulk setup:', error);
      toast.error('خطا در تنظیم یکجا');
    } finally {
      setProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(4px)'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="bg-white dark:bg-slate-900 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <span className="text-2xl">⚡</span>
            </div>
            <h2 className="text-2xl font-bold">تنظیم یکجا</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Progress Stepper */}
        <div className="px-6 pt-6 pb-4 bg-muted/30 dark:bg-slate-800/50 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            {[
              { num: 1, label: 'محصولات' },
              { num: 2, label: 'قانون' },
              { num: 3, label: 'نتیجه' }
            ].map((s, idx) => (
              <div key={s.num} className="flex items-center flex-1">
                <div className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all",
                  step >= s.num
                    ? "bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg scale-110"
                    : "bg-muted text-muted-foreground"
                )}>
                  {s.num}
                </div>
                <div className="flex-1 mx-2">
                  <p className={cn(
                    "text-xs font-medium",
                    step >= s.num ? "text-purple-600" : "text-muted-foreground"
                  )}>
                    {s.label}
                  </p>
                </div>
                {idx < 2 && (
                  <div className={cn(
                    "h-1 w-full rounded-full transition-all",
                    step > s.num ? "bg-purple-600" : "bg-muted"
                  )} />
                )}
              </div>
            ))}
          </div>
          <Progress value={(step / 3) * 100} className="h-2" />
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: انتخاب محصولات */}
          {step === 1 && (
            <div className="space-y-6">
              <h3 className="text-xl font-bold dark:text-white">کدوم محصولات رو تنظیم کنیم؟</h3>

              {/* همه محصولات */}
              <Card
                onClick={() => setSelectedOption('all')}
                className={cn(
                  "p-6 cursor-pointer transition-all duration-200",
                  selectedOption === 'all'
                    ? "ring-2 ring-purple-500 bg-purple-50 dark:bg-purple-900/20 shadow-lg"
                    : "hover:shadow-md"
                )}
              >
                <div className="flex items-center gap-4">
                  <div className={cn(
                    "w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all",
                    selectedOption === 'all'
                      ? "border-purple-600 bg-purple-600 scale-110"
                      : "border-input"
                  )}>
                    {selectedOption === 'all' && (
                      <CheckCircleIcon className="h-4 w-4 text-white" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-bold text-lg dark:text-white">همه محصولات موجود</h4>
                      <Badge className="bg-green-500 dark:bg-green-600">پیشنهادی</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      📦 {allProducts.length} محصول • 💡 سریع‌ترین راه
                    </p>
                  </div>
                </div>
              </Card>

              {/* دسته‌بندی */}
              <Card
                onClick={() => setSelectedOption('category')}
                className={cn(
                  "p-6 cursor-pointer transition-all",
                  selectedOption === 'category'
                    ? "ring-2 ring-purple-500 bg-purple-50 dark:bg-purple-900/20"
                    : "hover:shadow-md"
                )}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className={cn(
                    "w-6 h-6 rounded-full border-2 flex items-center justify-center",
                    selectedOption === 'category'
                      ? "border-purple-600 bg-purple-600"
                      : "border-input"
                  )}>
                    {selectedOption === 'category' && (
                      <CheckCircleIcon className="h-4 w-4 text-white" />
                    )}
                  </div>
                  <h4 className="font-bold">فقط یک دسته</h4>
                </div>

                {selectedOption === 'category' && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
                    {categories.map((cat) => (
                      <Card
                        key={cat.category_id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCategory(cat.category_id);
                        }}
                        className={cn(
                          "p-4 cursor-pointer transition-all text-center",
                          selectedCategory === cat.category_id
                            ? "ring-2 ring-purple-500 bg-purple-50 dark:bg-purple-900/20"
                            : "hover:shadow-md"
                        )}
                      >
                        <p className="font-semibold text-sm mb-1 dark:text-white">{cat.category_name}</p>
                        <Badge variant="outline" className="text-xs">
                          {cat.product_count} محصول
                        </Badge>
                      </Card>
                    ))}
                  </div>
                )}
              </Card>

              {/* تنظیمات پیشرفته */}
              <div>
                <button
                  onClick={() => setShowAdvancedProducts(!showAdvancedProducts)}
                  className="w-full flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition py-3"
                >
                  <span>تنظیمات پیشرفته (انتخاب دستی)</span>
                  {showAdvancedProducts ? (
                    <ChevronUpIcon className="h-4 w-4" />
                  ) : (
                    <ChevronDownIcon className="h-4 w-4" />
                  )}
                </button>

                {showAdvancedProducts && (
                  <Card className="mt-3 p-4 max-h-60 overflow-y-auto bg-muted/30">
                    <p className="text-xs text-muted-foreground mb-3">انتخاب دستی محصولات:</p>
                    <div className="space-y-2">
                      {allProducts.map((product) => (
                        <label
                          key={product.id}
                          className="flex items-center gap-3 p-2 hover:bg-background rounded cursor-pointer transition"
                        >
                          <input
                            type="checkbox"
                            checked={selectedProducts.includes(product.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedProducts([...selectedProducts, product.id]);
                                setSelectedOption('custom');
                              } else {
                                setSelectedProducts(selectedProducts.filter(id => id !== product.id));
                              }
                            }}
                            className="w-4 h-4 rounded border-input"
                          />
                          <span className="text-sm truncate dark:text-slate-200">{product.title}</span>
                        </label>
                      ))}
                    </div>
                  </Card>
                )}
              </div>
            </div>
          )}

          {/* Step 2: انتخاب قانون */}
          {step === 2 && (
            <div className="space-y-6">
              <h3 className="text-xl font-bold dark:text-white">چه قانونی اعمال بشه؟</h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {templates.map((template) => (
                  <Card
                    key={template.key}
                    onClick={() => setSelectedTemplate(template.key)}
                    className={cn(
                      "p-6 cursor-pointer transition-all duration-200 hover:shadow-lg",
                      selectedTemplate === template.key
                        ? "ring-2 ring-purple-500 bg-purple-50 dark:bg-purple-900/20 shadow-md"
                        : ""
                    )}
                  >
                    {selectedTemplate === template.key && (
                      <CheckCircleIcon className="h-6 w-6 text-purple-600 mb-3" />
                    )}
                    <div className="text-center">
                      <div className="text-4xl mb-3">{template.icon}</div>
                      <h4 className="font-bold mb-2 dark:text-white">{template.name}</h4>
                      {template.is_default && (
                        <Badge className="mb-2 bg-green-500">پیشنهادی</Badge>
                      )}
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {template.description}
                      </p>
                    </div>
                  </Card>
                ))}
              </div>

              {/* تنظیم دقیق */}
              <div>
                <Separator className="my-6" />
                <button
                  onClick={() => setShowAdvancedRule(!showAdvancedRule)}
                  className="w-full flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition py-3"
                >
                  <span>تنظیم دقیق قانون (پیشرفته)</span>
                  {showAdvancedRule ? (
                    <ChevronUpIcon className="h-4 w-4" />
                  ) : (
                    <ChevronDownIcon className="h-4 w-4" />
                  )}
                </button>

                {showAdvancedRule && (
                  <Card className="mt-3 p-6 bg-muted/30">
                    <h4 className="font-bold text-sm mb-4 dark:text-white">تنظیم دقیق قانون:</h4>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-medium mb-2 dark:text-slate-200">
                          آستانه (درصد):
                        </label>
                        <input
                          type="number"
                          value={customRule.threshold}
                          onChange={(e) => setCustomRule({...customRule, threshold: Number(e.target.value)})}
                          className="w-full h-9 px-3 py-2 text-sm rounded-md border border-input bg-background"
                          min="1"
                          max="100"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          مثلاً 15 یعنی اگه 15% بالاتر شد
                        </p>
                      </div>

                      <div>
                        <label className="block text-xs font-medium mb-2 dark:text-slate-200">
                          مقایسه با:
                        </label>
                        <select
                          value={customRule.comparison}
                          onChange={(e) => setCustomRule({...customRule, comparison: e.target.value})}
                          className="w-full h-9 px-3 py-2 text-sm rounded-md border border-input bg-background"
                        >
                          <option value="average">میانگین قیمت رقبا</option>
                          <option value="min">ارزان‌ترین رقیب</option>
                          <option value="max">گران‌ترین رقیب</option>
                        </select>
                      </div>

                      <Alert className="border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800">
                        <div>
                          <p className="text-xs font-semibold text-blue-800 dark:text-blue-200 mb-1">پیش‌نمایش:</p>
                          <p className="text-xs text-blue-700 dark:text-blue-300">
                            اگر قیمتم {customRule.threshold}% بیشتر از{' '}
                            {customRule.comparison === 'average' ? 'میانگین' : customRule.comparison === 'min' ? 'ارزان‌ترین' : 'گران‌ترین'}
                            {' '}شد، هشدار بده
                          </p>
                        </div>
                      </Alert>
                    </div>
                  </Card>
                )}
              </div>
            </div>
          )}

          {/* Step 3: پردازش */}
          {step === 3 && (
            <div className="space-y-6">
              {!summary ? (
                <div className="text-center space-y-6 py-8">
                  <div className="relative mx-auto w-24 h-24">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 opacity-20 animate-ping"></div>
                    <div className="relative w-24 h-24 rounded-full border-4 border-purple-200 border-t-purple-600 animate-spin"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-3xl">⚡</span>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-2xl font-bold mb-2 dark:text-white">در حال پردازش...</h3>
                    <p className="text-muted-foreground">
                      🔍 در حال پیدا کردن رقبا و اعمال قانون
                    </p>
                  </div>

                  <div className="max-w-md mx-auto space-y-2">
                    <Progress value={progress} className="h-3" />
                    <p className="text-sm text-muted-foreground">{progress}%</p>
                  </div>
                </div>
              ) : (
                <div className="text-center space-y-6 py-4">
                  <div className="mx-auto w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center shadow-lg">
                    <CheckCircleIcon className="h-12 w-12 text-white" />
                  </div>

                  <div>
                    <h3 className="text-3xl font-bold text-green-600 mb-2">تمام شد!</h3>
                    <p className="text-muted-foreground">تنظیمات با موفقیت اعمال شد</p>
                  </div>

                  <Card className="p-6 text-right bg-muted/30">
                    <h4 className="font-bold text-lg mb-4 flex items-center gap-2 dark:text-white">
                      <span className="text-2xl">📊</span>
                      <span>خلاصه نتایج:</span>
                    </h4>

                    <div className="space-y-3">
                      <div className="flex justify-between items-center p-3 bg-background rounded-lg">
                        <span className="text-muted-foreground">محصولات:</span>
                        <Badge variant="secondary" className="text-base px-3 py-1">
                          {summary.total_products}
                        </Badge>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-background rounded-lg">
                        <span className="text-muted-foreground">رقبای افزوده شده:</span>
                        <Badge className="bg-blue-600 text-base px-3 py-1">
                          {summary.competitors_added}
                        </Badge>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-background rounded-lg">
                        <span className="text-muted-foreground">قوانین اعمال شده:</span>
                        <Badge className="bg-purple-600 text-base px-3 py-1">
                          {summary.rules_applied}
                        </Badge>
                      </div>

                      {summary.alerts && summary.alerts.length > 0 && (
                        <div className="mt-4 pt-4 border-t">
                          <div className="flex justify-between items-center mb-3">
                            <span className="text-muted-foreground">⚠️ هشدارها:</span>
                            <Badge variant="destructive" className="text-base px-3 py-1">
                              {summary.alerts.length}
                            </Badge>
                          </div>
                          <div className="space-y-2 max-h-40 overflow-y-auto">
                            {summary.alerts.map((alert, i) => (
                              <Alert key={i} className="border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3">
                                <div>
                                  <p className="text-xs font-semibold text-red-800 dark:text-red-200">{alert.product_title}</p>
                                  <p className="text-xs text-red-600 dark:text-red-300 mt-1">{alert.alert_message}</p>
                                </div>
                              </Alert>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </Card>

                  <Button
                    onClick={onClose}
                    size="lg"
                    className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                  >
                    بازگشت به داشبورد
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Buttons */}
        {step < 3 && (
          <div className="border-t dark:border-slate-700 p-6 flex justify-between gap-4 flex-shrink-0 bg-muted/20 dark:bg-slate-800/30">
            {step > 1 && (
              <Button
                variant="outline"
                onClick={handleBack}
                className="px-8"
              >
                بازگشت
              </Button>
            )}

            <Button
              onClick={step === 2 ? handleSubmit : handleNext}
              disabled={processing}
              className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              size="lg"
            >
              {step === 2 ? '🚀 شروع پردازش' : 'ادامه ←'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkSetupWizardNew;
